import React, { useEffect, useState, useCallback } from 'react';
import { Header, Segment, Loader, Message, Icon, Button, Dropdown, Label, Divider } from 'semantic-ui-react';
import { useFormSubmissionsClient } from './formSubmissions.client';
import ImageWithAnnotations from './ImageWithAnnotations.component';
const AttachmentViewer = ({ submission }) => {
    const submissionsClient = useFormSubmissionsClient();
    const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);
    const [attachmentUrls, setAttachmentUrls] = useState({});
    const [loadingAttachment, setLoadingAttachment] = useState(false);
    const [attachmentErrors, setAttachmentErrors] = useState({});
    // Load signed URLs for attachments
    useEffect(() => {
        if (submission?.attachments?.length) {
            loadAttachmentUrls();
        }
    }, [submission]);
    const loadAttachmentUrls = async () => {
        if (!submission?.attachments?.length)
            return;
        setLoadingAttachment(true);
        setAttachmentErrors({}); // Clear previous errors
        const urls = {};
        const errors = {};
        try {
            // Load signed URLs for all attachments
            for (let i = 0; i < submission.attachments.length; i++) {
                const attachment = submission.attachments[i];
                try {
                    // If it's already a public URL or not a gs:// URL, use it directly
                    if (!attachment.fileUri.startsWith('gs://')) {
                        urls[i] = attachment.fileUri;
                    }
                    else {
                        // Get signed URL from backend
                        const signedUrl = await submissionsClient.getAttachmentSignedUrl(submission.submissionId, i);
                        urls[i] = signedUrl;
                    }
                }
                catch (attachmentError) {
                    console.error(`Error loading attachment ${i} (${attachment.fileName}):`, attachmentError);
                    // Determine specific error message based on error type
                    let errorMessage = 'Failed to load attachment';
                    if (attachmentError instanceof Error) {
                        if (attachmentError.message.includes('404') || attachmentError.message.includes('not found')) {
                            errorMessage = 'Attachment file not found';
                        }
                        else if (attachmentError.message.includes('403') || attachmentError.message.includes('forbidden')) {
                            errorMessage = 'Access denied to attachment';
                        }
                        else if (attachmentError.message.includes('credentials') || attachmentError.message.includes('authentication')) {
                            errorMessage = 'Authentication error - please contact support';
                        }
                        else if (attachmentError.message.includes('network') || attachmentError.message.includes('timeout')) {
                            errorMessage = 'Network error - please try again';
                        }
                        else if (attachmentError.message.includes('Invalid Google Storage URL')) {
                            errorMessage = 'Invalid file storage URL';
                        }
                    }
                    errors[i] = errorMessage;
                }
            }
            setAttachmentUrls(urls);
            setAttachmentErrors(errors);
            // Don't set general error - let individual attachments handle their own errors
        }
        catch (error) {
            console.error('Error loading attachment URLs:', error);
            // Only set general error for catastrophic failures, not individual attachment failures
            setAttachmentErrors({ 0: 'Failed to load attachments. Please try again.' });
        }
        finally {
            setLoadingAttachment(false);
        }
    };
    // Memoized error handler to prevent re-render loops
    const handleAttachmentError = useCallback((attachmentIndex, errorMessage) => {
        setAttachmentErrors(prev => ({
            ...prev,
            [attachmentIndex]: errorMessage
        }));
    }, []);
    const renderAttachmentViewer = () => {
        if (!submission?.attachments?.length) {
            return (React.createElement(Message, { info: true },
                React.createElement(Icon, { name: "info circle" }),
                "No attachments found for this submission."));
        }
        if (loadingAttachment) {
            return (React.createElement(Segment, { style: { height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                React.createElement(Loader, { active: true, inline: "centered" }, "Loading attachments...")));
        }
        const attachment = submission.attachments[selectedAttachmentIndex];
        const attachmentIndex = selectedAttachmentIndex;
        const signedUrl = attachmentUrls[attachmentIndex];
        const attachmentError = attachmentErrors[attachmentIndex];
        // If there's an error for this specific attachment, show error message
        if (attachmentError) {
            return (React.createElement(Message, { error: true },
                React.createElement(Icon, { name: "exclamation triangle" }),
                React.createElement("div", null,
                    React.createElement("strong", null,
                        "Error loading ",
                        attachment.fileName,
                        ":"),
                    React.createElement("br", null),
                    attachmentError,
                    React.createElement("br", null),
                    React.createElement(Button, { size: "small", primary: true, onClick: () => {
                            // Clear error and retry
                            setAttachmentErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[attachmentIndex];
                                return newErrors;
                            });
                            loadAttachmentUrls();
                        }, loading: loadingAttachment, style: { marginTop: '10px' } },
                        React.createElement(Icon, { name: "refresh" }),
                        "Retry"))));
        }
        if (!signedUrl) {
            return (React.createElement(Message, { warning: true },
                React.createElement(Icon, { name: "clock outline" }),
                "Loading ",
                attachment.fileName,
                "..."));
        }
        try {
            // Determine file type
            const fileType = attachment.mimeType || '';
            const fileName = attachment.fileName || '';
            const isImage = fileType.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
            const isPDF = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
            return (React.createElement("div", null,
                isImage && (React.createElement("div", { style: { textAlign: 'center' } },
                    React.createElement(ImageWithAnnotations, { imageUrl: signedUrl, imageName: attachment.fileName, visionData: attachment.visionData, onError: (errorMessage) => {
                            // Handle image load error
                            console.warn(`Failed to load image: ${attachment.fileName}`);
                            handleAttachmentError(attachmentIndex, errorMessage);
                        }, onLoad: () => {
                            // Clear any previous errors for this attachment when image loads successfully
                            setAttachmentErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[attachmentIndex];
                                return newErrors;
                            });
                        } }))),
                isPDF && (React.createElement("div", { style: { height: '600px', border: '1px solid #ddd', borderRadius: '4px' } },
                    React.createElement("iframe", { src: signedUrl, width: "100%", height: "100%", style: { border: 'none', borderRadius: '4px' }, title: attachment.fileName, onError: (e) => {
                            // Prevent the error from bubbling up
                            e.preventDefault();
                            e.stopPropagation();
                            // Handle PDF load error
                            console.warn(`Failed to load PDF: ${attachment.fileName}`);
                            handleAttachmentError(attachmentIndex, 'Failed to load PDF - file may be corrupted or your browser does not support PDF viewing');
                        }, onLoad: () => {
                            // Clear any previous errors for this attachment when PDF loads successfully
                            setAttachmentErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[attachmentIndex];
                                return newErrors;
                            });
                        } }))),
                !isImage && !isPDF && (React.createElement(Message, { info: true },
                    React.createElement(Icon, { name: "file outline" }),
                    React.createElement("div", null,
                        React.createElement("strong", null, "File Preview Not Available"),
                        React.createElement("p", null,
                            "This file type (",
                            fileType || 'unknown',
                            ") cannot be previewed in the browser."),
                        React.createElement("p", null, "Click the download button above to save the file to your device.")))),
                attachment.extractedText && (React.createElement("div", { style: { marginTop: '20px' } },
                    React.createElement(Divider, null),
                    React.createElement(Header, { as: "h4" },
                        React.createElement(Icon, { name: "text cursor" }),
                        "Extracted Text"),
                    React.createElement(Segment, null,
                        React.createElement("pre", { style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }, attachment.extractedText))))));
        }
        catch (renderError) {
            console.error('Error rendering attachment:', renderError);
            return (React.createElement(Message, { error: true },
                React.createElement(Icon, { name: "exclamation triangle" }),
                React.createElement("div", null,
                    React.createElement("strong", null, "Error displaying attachment:"),
                    React.createElement("br", null),
                    "An unexpected error occurred while trying to display this attachment.",
                    React.createElement("br", null),
                    React.createElement(Button, { size: "small", primary: true, onClick: () => window.location.reload(), style: { marginTop: '10px' } },
                        React.createElement(Icon, { name: "refresh" }),
                        "Refresh Page"))));
        }
    };
    const attachments = submission.attachments || [];
    const hasAttachments = attachments.length > 0;
    const selectedAttachment = hasAttachments ? attachments[selectedAttachmentIndex] : null;
    const attachmentOptions = attachments.map((attachment, index) => ({
        key: index,
        value: index,
        text: `${attachment.fileName} (${attachment.mimeType})`,
        icon: attachmentErrors[index] ? 'warning circle' :
            attachment.mimeType.startsWith('image/') ? 'image' :
                attachment.mimeType === 'application/pdf' ? 'file pdf' : 'file',
        // Add visual indicator for errors
        content: (React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
            React.createElement(Icon, { name: attachmentErrors[index] ? 'warning circle' :
                    attachment.mimeType.startsWith('image/') ? 'image' :
                        attachment.mimeType === 'application/pdf' ? 'file pdf' : 'file', color: attachmentErrors[index] ? 'red' : undefined }),
            React.createElement("span", { style: {
                    color: attachmentErrors[index] ? '#db2828' : undefined,
                    textDecoration: attachmentErrors[index] ? 'line-through' : undefined
                } },
                attachment.fileName,
                " (",
                attachment.mimeType,
                ")"),
            attachmentErrors[index] && (React.createElement(Label, { size: "mini", color: "red", style: { marginLeft: 'auto' } }, "Error"))))
    }));
    return (React.createElement("div", null, hasAttachments ? (React.createElement(React.Fragment, null,
        attachments.length > 1 && (React.createElement("div", { style: { marginBottom: '1rem' } },
            React.createElement(Dropdown, { fluid: true, selection: true, options: attachmentOptions, value: selectedAttachmentIndex, onChange: (e, { value }) => setSelectedAttachmentIndex(value), placeholder: "Select attachment to view" }))),
        selectedAttachment && (React.createElement(Segment, null,
            React.createElement("div", { style: { marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                React.createElement("div", null,
                    React.createElement(Header, { as: "h4", style: { margin: 0 } }, selectedAttachment.fileName),
                    React.createElement(Label, { size: "small" }, selectedAttachment.mimeType)),
                React.createElement(Button, { as: "a", href: attachmentUrls[selectedAttachmentIndex] || selectedAttachment.fileUri, target: "_blank", size: "small", icon: true, labelPosition: "left", disabled: !attachmentUrls[selectedAttachmentIndex] && loadingAttachment },
                    React.createElement(Icon, { name: "download" }),
                    "Download")),
            React.createElement(Divider, null),
            renderAttachmentViewer())))) : (React.createElement(Segment, { placeholder: true },
        React.createElement(Header, { icon: true },
            React.createElement(Icon, { name: "paperclip" }),
            "No Attachments"),
        React.createElement("p", null, "This submission doesn't have any file attachments.")))));
};
export default AttachmentViewer;
